<template>
  <div class="cart-sidebar-empty">
    <div class="cart-sidebar-empty__icon">
      <FontAwesomeIcon
        :icon="appIcons.faCartShopping"
        aria-hidden="true"
      />
    </div>
    <div class="cart-sidebar-empty__heading">{{ heading }}</div>
    <div class="cart-sidebar-empty__subtext">{{ subtext }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { mapState } from 'pinia'

import useAppIcons from '@/composables/appIcons'
import { makeContentModuleGetter } from '@/composables/contentful'

// Content-item identifiers in the `rewards-tracker` content module, with in-code fallbacks so the copy
// still renders (and stays grep-able) if the module is absent during rollout. Same pattern and
// namespace as the tracker headings in `useRewards`.
const EMPTY_CART_COPY = {
  heading: {
    id: 'rewards-tracker-empty-cart-heading',
    fallback: 'Your cart is empty'
  },
  subtext: {
    id: 'rewards-tracker-empty-cart-subtext',
    fallback: 'Select a package to start unlocking rewards.'
  }
} as const

/**
 * Empty-cart state for the cart flyout — Gap 6, Figma desktop `15946:29361` / mobile `15946:29142`.
 *
 * Replaces the hard-coded "There are currently no items in your cart." string. The board's copy and
 * the in-code fallbacks are deliberately identical, so an unauthored module renders the designed text
 * rather than placeholder wording.
 */
export default defineComponent({
  name: 'AppCartSidebarEmptyState',
  setup() {
    return { ...useAppIcons() }
  },
  computed: {
    ...mapState(useAppContentStore, ['rewardsTrackerContent']),
    heading(): string {
      return this.read(EMPTY_CART_COPY.heading)
    },
    subtext(): string {
      return this.read(EMPTY_CART_COPY.subtext)
    }
  },
  methods: {
    read({ id, fallback }: { id: string; fallback: string }): string {
      return makeContentModuleGetter(this.rewardsTrackerContent)(id, fallback)
    }
  }
})
</script>

<style scoped lang="scss">
.cart-sidebar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem; // 12px

  // 60px circle on gray-100 with a 28px gray-300 cart glyph (Figma `15946:29362`).
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 999px;
    background-color: $cady-color-gray-100;
    // Sizes the Font Awesome glyph, which renders at 1em.
    font-size: 1.75rem; // 28px
    line-height: 1;
    color: $cady-color-gray-300;
  }

  // Mobile steps down to `body lg bold` rather than a heading step — the board uses 18/28 here
  // (`15946:29145`) against 30px on desktop (`15946:29364`).
  &__heading {
    font-size: $cady-type-body-lg-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-body-lg-line-height;
    color: $cady-color-black;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h4-desktop-size;
      line-height: $cady-type-heading-line-height;
    }
  }

  &__subtext {
    font-size: $cady-type-body-nm-size;
    line-height: $cady-type-body-nm-line-height;
    color: $cady-color-black;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-body-lg-size;
      line-height: $cady-type-body-lg-line-height;
    }
  }
}
</style>
